import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Home Realm Discovery Login Component.
 *
 * A reusable HRD login page component that can be styled and customized.
 */
import { helpOutlineRounded } from '@aero/icons/help-outline-rounded';
import { openInNewRounded } from '@aero/icons/open-in-new-rounded';
import { AeroIcon, Button, Card, CardContent, CardFooter, CardHeader, CardTitle, Checkbox, DropdownMenu, DropdownMenuContent, DropdownMenuGroup, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger, Field, FieldError, FieldGroup, FieldLabel, Input, Spinner } from '@aero/ui';
import { useMsal } from '@azure/msal-react';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { DEFAULT_LOGIN_SCOPES, getDomainFromEmail, resolveAuthorityForEmail } from '../config';
import { clearRememberedEmail, getRememberedEmail, hasRememberedEmail, setPostLoginPath, setRememberedEmail } from '../storage';
/**
 * Simple email validation regex.
 * Checks for: something@something.something
 */
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
/**
 * Validates an email address.
 */
function validateEmail(email) {
    if (!email.trim()) {
        return 'Email is required';
    }
    if (!EMAIL_REGEX.test(email)) {
        return 'Invalid email address';
    }
    return null;
}
/**
 * Home Realm Discovery Login Page Component.
 *
 * This component shows a login form where users enter their email address,
 * which determines which tenant/authority to use for authentication.
 *
 * @example Basic usage
 * ```tsx
 * <HrdLogin
 *   currentPath={location.pathname}
 *   defaultAuthority={authority}
 * />
 * ```
 *
 * @example With custom domain mappings
 * ```tsx
 * <HrdLogin
 *   currentPath={location.pathname}
 *   defaultAuthority={authority}
 *   domainMappings={[
 *     { domain: 'contoso.com', authority: 'https://login.microsoftonline.com/contoso-tenant-id' }
 *   ]}
 * />
 * ```
 */
export function HrdLogin({ currentPath = '/', loginPath = '/login', domainMappings = [], defaultAuthority, storageKeyPrefix, containerStyle, cardStyle, header, footer, showRememberMe = true, hrdHeaderMenu, hrdFooterMenu, onLoginStart, onLoginError, onNonSsoLogin }) {
    const { instance } = useMsal();
    const storageOptions = storageKeyPrefix ? { keyPrefix: storageKeyPrefix } : undefined;
    // Form state
    const [email, setEmail] = useState(() => getRememberedEmail(storageOptions) ?? '');
    const [rememberMe, setRememberMe] = useState(() => hasRememberedEmail(storageOptions));
    const [emailError, setEmailError] = useState(null);
    const [touched, setTouched] = useState(false);
    const [isLoggingIn, setIsLoggingIn] = useState(false);
    // Extract domain for display
    const domain = useMemo(() => getDomainFromEmail(email), [email]);
    const handleEmailChange = (e) => {
        const value = e.target.value;
        setEmail(value);
        // Clear error when user starts typing (if they've already touched the field)
        if (touched && emailError) {
            setEmailError(validateEmail(value));
        }
    };
    const handleEmailBlur = () => {
        setTouched(true);
        setEmailError(validateEmail(email));
    };
    const handleNonSsoLogin = async () => {
        setIsLoggingIn(true);
        onNonSsoLogin?.();
        // Store current path for post-login redirect
        const returnPath = currentPath !== loginPath ? currentPath : '/';
        setPostLoginPath(returnPath);
        try {
            await instance.loginRedirect({
                scopes: DEFAULT_LOGIN_SCOPES,
                redirectStartPage: returnPath
            });
        }
        catch (error) {
            console.error('Login failed:', error);
            onLoginError?.(error instanceof Error ? error : new Error(String(error)));
            setIsLoggingIn(false);
        }
    };
    const handleSubmit = async (e) => {
        e.preventDefault();
        // Validate before submitting
        const error = validateEmail(email);
        if (error) {
            setTouched(true);
            setEmailError(error);
            return;
        }
        setIsLoggingIn(true);
        onLoginStart?.(email);
        // Store current path for post-login redirect
        const returnPath = currentPath !== loginPath ? currentPath : '/';
        setPostLoginPath(returnPath);
        const authority = resolveAuthorityForEmail(email, domainMappings, defaultAuthority);
        // Handle remember me preference
        if (rememberMe) {
            setRememberedEmail(email, storageOptions);
        }
        else {
            clearRememberedEmail(storageOptions);
        }
        try {
            await instance.loginRedirect({
                scopes: DEFAULT_LOGIN_SCOPES,
                authority,
                loginHint: email,
                prompt: 'select_account',
                extraQueryParameters: domain ? { domain_hint: domain } : undefined
            });
        }
        catch (error) {
            console.error('Login failed:', error);
            onLoginError?.(error instanceof Error ? error : new Error(String(error)));
            setIsLoggingIn(false);
        }
    };
    const defaultContainerStyle = {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'space-between',
        minHeight: '100vh',
        background: '#ffffff'
    };
    const defaultCardStyle = {
        width: '100%',
        maxWidth: '28rem'
    };
    const hasError = touched && emailError;
    return (_jsxs("div", { style: { ...defaultContainerStyle, ...containerStyle }, children: [_jsx("div", { style: {
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-end',
                    width: '100%',
                    paddingRight: '1.5rem',
                    height: '4rem',
                    background: '#ffffff',
                    borderBottom: '1px solid #e2e8f0'
                }, children: hrdHeaderMenu && hrdHeaderMenu.length > 0 && (_jsxs(DropdownMenu, { children: [_jsx(DropdownMenuTrigger, { render: _jsx(Button, { variant: "outline", size: "icon", className: "rounded-full", children: _jsx(AeroIcon, { style: { width: '1.5rem', height: '1.5rem' }, icon: helpOutlineRounded }) }) }), _jsx(DropdownMenuContent, { align: "end", children: _jsx(DropdownMenuGroup, { children: hrdHeaderMenu.map((item, index) => {
                                    if (item.isHeading) {
                                        return _jsx(DropdownMenuLabel, { children: item.label }, item.id);
                                    }
                                    if (item.isDivider) {
                                        return _jsx(DropdownMenuSeparator, {}, item.id || `divider-${index}`);
                                    }
                                    const isExternal = item.path?.startsWith('http://') || item.path?.startsWith('https://');
                                    return (_jsx(DropdownMenuItem, { children: isExternal ? (_jsxs("a", { href: item.path, target: "_blank", rel: "noopener noreferrer", children: [item.label, _jsx(AeroIcon, { icon: openInNewRounded })] })) : (_jsx(Link, { to: item.path ?? '', children: item.label })) }, item.id));
                                }) }) })] })) }), _jsxs(Card, { style: { ...defaultCardStyle, ...cardStyle }, children: [header ?? (_jsx(CardHeader, { children: _jsx(CardTitle, { style: { textAlign: 'center', fontSize: '1.5rem', lineHeight: '2rem' }, children: "Welcome Back" }) })), _jsx(CardContent, { children: _jsx("form", { id: "hrd-login-form", onSubmit: handleSubmit, style: { display: 'flex', flexDirection: 'column', gap: '1rem' }, children: _jsxs(FieldGroup, { children: [_jsxs(Field, { "data-invalid": !!hasError, children: [_jsx(FieldLabel, { htmlFor: "hrd-email", children: "Username/Email" }), _jsx(Input, { id: "hrd-email", type: "email", value: email, onChange: handleEmailChange, onBlur: handleEmailBlur, placeholder: "name@company.com", autoComplete: "email", autoFocus: true, disabled: isLoggingIn, "aria-invalid": hasError ? true : undefined, "aria-describedby": hasError ? 'hrd-email-error' : undefined }), hasError && (_jsx(FieldError, { id: "hrd-email-error", errors: [{ message: emailError }] }))] }), showRememberMe && (_jsxs(Field, { orientation: "horizontal", children: [_jsx(Checkbox, { id: "remember-email", disabled: isLoggingIn, checked: rememberMe, onCheckedChange: checked => setRememberMe(checked === true) }), _jsx(FieldLabel, { htmlFor: "remember-email", children: "Remember username/email" })] }))] }) }) }), _jsxs(CardFooter, { style: { display: 'flex', flexDirection: 'column', gap: '1.25rem' }, children: [_jsx(Field, { orientation: "horizontal", style: { justifyContent: 'center' }, children: _jsx(Button, { type: "submit", form: "hrd-login-form", disabled: isLoggingIn, children: isLoggingIn ? (_jsxs("span", { style: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }, children: [_jsx(Spinner, { style: { width: '1rem', height: '1rem' } }), "Signing in..."] })) : ('Continue') }) }), domain && (_jsxs("p", { style: { fontSize: '0.875rem', lineHeight: '1.25rem', color: '#6b7280', textAlign: 'center' }, children: ["Signing in with ", _jsx("strong", { style: { fontWeight: 600 }, children: domain })] })), _jsxs("p", { style: { fontSize: '0.875rem', lineHeight: '1.25rem', color: '#6b7280', textAlign: 'center' }, children: ["SSO Issues?", ' ', _jsx(Button, { variant: "link", style: { padding: 0, height: 'auto' }, onClick: handleNonSsoLogin, children: "Log in without SSO" })] }), footer] })] }), _jsx("div", { style: {
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    width: '100%',
                    height: '4rem',
                    background: '#efeff3',
                    borderTop: '1px solid #e2e8f0',
                    padding: '1.5rem 1rem'
                }, children: hrdFooterMenu && hrdFooterMenu.length > 0 && (_jsx("ul", { style: {
                        fontSize: '0.875rem',
                        lineHeight: '1.25rem',
                        color: '#6b7280',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-start',
                        gap: '2rem',
                        listStyleType: 'none'
                    }, children: hrdFooterMenu
                        .filter(item => !item.isHeading && !item.isDivider)
                        .map(item => {
                        const isExternal = item.path?.startsWith('http://') || item.path?.startsWith('https://');
                        return (_jsx("li", { children: isExternal ? (_jsx("a", { href: item.path, target: "_blank", rel: "noopener noreferrer", children: item.label })) : (_jsx(Link, { to: item.path ?? '', children: item.label })) }, item.id));
                    }) })) })] }));
}
//# sourceMappingURL=hrd-login.js.map